/**
 * VisualChangeTracker - Google Docs style change tracking and visualization
 *
 * Features:
 * - Collaborator list with change counts
 * - User-specific change filtering
 * - Visual change overlay with colors
 * - Timestamped change navigation
 * - Change details tooltips
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  UserIcon,
  ClockIcon,
  EyeIcon,
  EyeSlashIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  MinusIcon,
  PencilIcon,
  ArrowsRightLeftIcon,
  SwatchIcon,
  Squares2X2Icon,
  ChatBubbleLeftIcon,
  TagIcon,
  TableCellsIcon,
  PhotoIcon,
  LinkIcon,
  ScaleIcon,
  DocumentTextIcon,
  IdentificationIcon,
  DocumentIcon,
  HashtagIcon,
  HandThumbUpIcon,
  HandThumbDownIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export interface VisualChange {
  id: string;
  type: 'insert' | 'delete' | 'format' | 'move' | 'replace' | 'style' | 'structure' |
        'annotation' | 'metadata' | 'table' | 'image' | 'link' | 'citation' | 'clause' |
        'signature' | 'header_footer' | 'page_break' | 'line_number' | 'suggestion' |
        'acceptance' | 'rejection' | 'comment';
  userId: string;
  userName: string;
  userEmail: string;
  timestamp: Date;
  position: { start: number; end: number };
  content?: string;
  originalContent?: string;
  newContent?: string;
  changeData: any;
  positionData: any;
  confidence?: number; // Confidence score (0-1) for the change detection
  status?: 'pending' | 'accepted' | 'rejected'; // Change review status
}

interface CollaboratorSummary {
  userId: string;
  userName: string;
  userEmail: string;
  avatar?: string;
  totalChanges: number;
  lastChangeAt: Date;
  changeBreakdown: {
    // Basic operations
    inserts: number;
    deletes: number;
    formats: number;
    moves: number;

    // Extended operations
    replaces: number;
    styles: number;
    structures: number;
    annotations: number;
    metadata: number;
    tables: number;
    images: number;
    links: number;

    // Legal document specific
    citations: number;
    clauses: number;
    signatures: number;
    headerFooters: number;
    pageBreaks: number;
    lineNumbers: number;

    // Collaboration types
    suggestions: number;
    acceptances: number;
    rejections: number;
    comments: number;
  };
  color: string; // Assigned color for this user's changes
}

// Advanced filtering and navigation interfaces
interface ChangeFilter {
  changeTypes: string[]; // Array of change types to show
  confidenceMin: number; // Minimum confidence threshold (0-1)
  legalSignificance: 'all' | 'legal-only' | 'non-legal'; // Legal significance filter
  timeRange: 'all' | 'last-hour' | 'last-day' | 'last-week'; // Time-based filter
  users: string[]; // Array of user IDs to show
  status: 'all' | 'pending' | 'accepted' | 'rejected'; // Change status filter
}

interface NavigationState {
  currentChangeIndex: number;
  filteredChanges: VisualChange[];
  selectedChangeIds: string[]; // For bulk operations
}

interface BulkAction {
  type: 'accept' | 'reject' | 'delete';
  changeIds: string[];
}

interface VisualChangeTrackerProps {
  documentId: string;
  versionId?: string;
  documentContent: string;
  onChangeSelection?: (changeId: string | null) => void;
  onUserSelection?: (userId: string | null) => void;
  onChangesLoaded?: (changes: VisualChange[]) => void;
  className?: string;
}

// ============================================================================
// USER COLOR ASSIGNMENT
// ============================================================================

const USER_COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Yellow
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#F97316', // Orange
  '#06B6D4', // Cyan
  '#84CC16', // Lime
  '#EC4899', // Pink
  '#6B7280', // Gray
];

const getUserColor = (userId: string): string => {
  const hash = userId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return USER_COLORS[hash % USER_COLORS.length];
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const VisualChangeTracker: React.FC<VisualChangeTrackerProps> = ({
  documentId,
  versionId,
  documentContent,
  onChangeSelection,
  onUserSelection,
  onChangesLoaded,
  className = ''
}) => {
  // State
  const [changes, setChanges] = useState<VisualChange[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedChangeIndex, setSelectedChangeIndex] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Advanced filtering and navigation state
  const [filter, setFilter] = useState<ChangeFilter>({
    changeTypes: [], // Empty means show all
    confidenceMin: 0.5, // 50% minimum confidence
    legalSignificance: 'all',
    timeRange: 'all',
    users: [], // Empty means show all users
    status: 'all'
  });

  const [navigation, setNavigation] = useState<NavigationState>({
    currentChangeIndex: 0,
    filteredChanges: [],
    selectedChangeIds: []
  });

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Filtering logic
  const filteredChanges = useMemo(() => {
    let filtered = [...changes];

    // Filter by change types
    if (filter.changeTypes.length > 0) {
      filtered = filtered.filter(change => filter.changeTypes.includes(change.type));
    }

    // Filter by confidence level
    filtered = filtered.filter(change => (change.confidence || 1) >= filter.confidenceMin);

    // Filter by legal significance
    if (filter.legalSignificance === 'legal-only') {
      const legalTypes = ['citation', 'clause', 'signature', 'header_footer', 'page_break', 'line_number'];
      filtered = filtered.filter(change => legalTypes.includes(change.type));
    } else if (filter.legalSignificance === 'non-legal') {
      const legalTypes = ['citation', 'clause', 'signature', 'header_footer', 'page_break', 'line_number'];
      filtered = filtered.filter(change => !legalTypes.includes(change.type));
    }

    // Filter by time range
    if (filter.timeRange !== 'all') {
      const now = new Date();
      const cutoffTime = new Date();

      switch (filter.timeRange) {
        case 'last-hour':
          cutoffTime.setHours(now.getHours() - 1);
          break;
        case 'last-day':
          cutoffTime.setDate(now.getDate() - 1);
          break;
        case 'last-week':
          cutoffTime.setDate(now.getDate() - 7);
          break;
      }

      filtered = filtered.filter(change => new Date(change.timestamp) >= cutoffTime);
    }

    // Filter by users (including selectedUserId for backward compatibility)
    if (filter.users.length > 0) {
      filtered = filtered.filter(change => filter.users.includes(change.userId));
    } else if (selectedUserId) {
      // Legacy user selection - filter by selected user
      filtered = filtered.filter(change => change.userId === selectedUserId);
    }

    // Filter by status
    if (filter.status !== 'all') {
      filtered = filtered.filter(change => change.status === filter.status);
    }

    return filtered.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }, [changes, filter, selectedUserId]);

  // Update navigation state when filtered changes change
  useEffect(() => {
    setNavigation(prev => ({
      ...prev,
      filteredChanges,
      currentChangeIndex: Math.min(prev.currentChangeIndex, Math.max(0, filteredChanges.length - 1))
    }));
  }, [filteredChanges]);

  // Computed values (based on filtered changes)
  const collaborators = useMemo(() => {
    const userMap = new Map<string, CollaboratorSummary>();

    filteredChanges.forEach(change => {
      const existing = userMap.get(change.userId);
      if (existing) {
        existing.totalChanges++;
        // Update the appropriate counter based on change type
        switch (change.type) {
          case 'insert': existing.changeBreakdown.inserts++; break;
          case 'delete': existing.changeBreakdown.deletes++; break;
          case 'format': existing.changeBreakdown.formats++; break;
          case 'move': existing.changeBreakdown.moves++; break;
          case 'replace': existing.changeBreakdown.replaces++; break;
          case 'style': existing.changeBreakdown.styles++; break;
          case 'structure': existing.changeBreakdown.structures++; break;
          case 'annotation': existing.changeBreakdown.annotations++; break;
          case 'metadata': existing.changeBreakdown.metadata++; break;
          case 'table': existing.changeBreakdown.tables++; break;
          case 'image': existing.changeBreakdown.images++; break;
          case 'link': existing.changeBreakdown.links++; break;
          case 'citation': existing.changeBreakdown.citations++; break;
          case 'clause': existing.changeBreakdown.clauses++; break;
          case 'signature': existing.changeBreakdown.signatures++; break;
          case 'header_footer': existing.changeBreakdown.headerFooters++; break;
          case 'page_break': existing.changeBreakdown.pageBreaks++; break;
          case 'line_number': existing.changeBreakdown.lineNumbers++; break;
          case 'suggestion': existing.changeBreakdown.suggestions++; break;
          case 'acceptance': existing.changeBreakdown.acceptances++; break;
          case 'rejection': existing.changeBreakdown.rejections++; break;
          case 'comment': existing.changeBreakdown.comments++; break;
        }
        if (change.timestamp > existing.lastChangeAt) {
          existing.lastChangeAt = change.timestamp;
        }
      } else {
        userMap.set(change.userId, {
          userId: change.userId,
          userName: change.userName,
          userEmail: change.userEmail,
          totalChanges: 1,
          lastChangeAt: change.timestamp,
          changeBreakdown: {
            // Basic operations
            inserts: change.type === 'insert' ? 1 : 0,
            deletes: change.type === 'delete' ? 1 : 0,
            formats: change.type === 'format' ? 1 : 0,
            moves: change.type === 'move' ? 1 : 0,

            // Extended operations
            replaces: change.type === 'replace' ? 1 : 0,
            styles: change.type === 'style' ? 1 : 0,
            structures: change.type === 'structure' ? 1 : 0,
            annotations: change.type === 'annotation' ? 1 : 0,
            metadata: change.type === 'metadata' ? 1 : 0,
            tables: change.type === 'table' ? 1 : 0,
            images: change.type === 'image' ? 1 : 0,
            links: change.type === 'link' ? 1 : 0,

            // Legal document specific
            citations: change.type === 'citation' ? 1 : 0,
            clauses: change.type === 'clause' ? 1 : 0,
            signatures: change.type === 'signature' ? 1 : 0,
            headerFooters: change.type === 'header_footer' ? 1 : 0,
            pageBreaks: change.type === 'page_break' ? 1 : 0,
            lineNumbers: change.type === 'line_number' ? 1 : 0,

            // Collaboration types
            suggestions: change.type === 'suggestion' ? 1 : 0,
            acceptances: change.type === 'acceptance' ? 1 : 0,
            rejections: change.type === 'rejection' ? 1 : 0,
            comments: change.type === 'comment' ? 1 : 0,
          },
          color: getUserColor(change.userId)
        });
      }
    });

    return Array.from(userMap.values()).sort((a, b) =>
      b.lastChangeAt.getTime() - a.lastChangeAt.getTime()
    );
  }, [filteredChanges]);

  const selectedCollaborator = useMemo(() => {
    return collaborators.find(c => c.userId === selectedUserId);
  }, [collaborators, selectedUserId]);

  // Load changes from API
  useEffect(() => {
    loadChanges();
  }, [documentId, versionId]);

  // Keyboard navigation (J/K keys for next/previous change)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle keys when the modal is focused and no input is active
      if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'SELECT') {
        return;
      }

      switch (event.key.toLowerCase()) {
        case 'j': // Next change
          event.preventDefault();
          setNavigation(prev => {
            const nextIndex = Math.min(prev.currentChangeIndex + 1, prev.filteredChanges.length - 1);
            const nextChange = prev.filteredChanges[nextIndex];
            if (nextChange && onChangeSelection) {
              onChangeSelection(nextChange.id);
            }
            return { ...prev, currentChangeIndex: nextIndex };
          });
          break;

        case 'k': // Previous change
          event.preventDefault();
          setNavigation(prev => {
            const prevIndex = Math.max(prev.currentChangeIndex - 1, 0);
            const prevChange = prev.filteredChanges[prevIndex];
            if (prevChange && onChangeSelection) {
              onChangeSelection(prevChange.id);
            }
            return { ...prev, currentChangeIndex: prevIndex };
          });
          break;

        case 'escape': // Clear selection
          event.preventDefault();
          if (onChangeSelection) {
            onChangeSelection(null);
          }
          setNavigation(prev => ({ ...prev, selectedChangeIds: [] }));
          break;

        case 'a': // Select all visible changes (for bulk operations)
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            setNavigation(prev => ({
              ...prev,
              selectedChangeIds: prev.filteredChanges.map(c => c.id)
            }));
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [navigation.filteredChanges, onChangeSelection]);

  const loadChanges = async () => {
    setIsLoading(true);
    setError(null);

    try {
      console.log('[VisualChangeTracker] Loading changes for document:', documentId);

      // Try multiple token storage keys used by different parts of the system
      const token = localStorage.getItem('webelieve_access_token') ||
                   localStorage.getItem('jwt_token') ||
                   localStorage.getItem('access_token');

      if (!token) {
        throw new Error('No authentication token found. Please ensure you are logged in.');
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // First, get collaborator information to map user IDs to names
      console.log('[VisualChangeTracker] Loading collaborator information...');
      const collaborationResponse = await fetch(`/api/v1/documents/${documentId}/collaboration`, { headers });
      const userMap = new Map<string, { name: string; email: string }>();

      if (collaborationResponse.ok) {
        const collaborationData = await collaborationResponse.json();
        const collaborators = collaborationData?.data?.data?.collaborators || collaborationData?.data?.collaborators || [];

        collaborators.forEach((collaborator: any) => {
          userMap.set(collaborator.id, {
            name: collaborator.name || collaborator.email?.split('@')[0] || 'Unknown User',
            email: collaborator.email || 'unknown@example.com'
          });
        });

        console.log('[VisualChangeTracker] Loaded user map:', Object.fromEntries(userMap));
      }

      // Now get the changes
      const url = `/api/v1/documents/${documentId}/changes`;
      console.log('[VisualChangeTracker] Making request to:', url);

      const response = await fetch(url, { headers });

      console.log('[VisualChangeTracker] Response status:', response.status, response.statusText);

      if (!response.ok) {
        // Try to get error details from response
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorText = await response.text();
          console.log('[VisualChangeTracker] Error response body:', errorText);

          // Check if it's JSON error response
          if (errorText.startsWith('{')) {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.message || errorData.error || errorMessage;
          } else if (errorText.includes('<html>')) {
            errorMessage = 'Server returned HTML page (likely 404 or server error)';
          }
        } catch (parseError) {
          console.log('[VisualChangeTracker] Could not parse error response');
        }

        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('[VisualChangeTracker] Response data:', data);

      // Handle nested response structure: data.data.data.changes
      let changesArray = null;
      if (data.success && data.data?.data?.changes) {
        changesArray = data.data.data.changes;
      } else if (data.success && data.data?.changes) {
        changesArray = data.data.changes;
      } else if (data.changes) {
        changesArray = data.changes;
      }

      if (changesArray && Array.isArray(changesArray)) {
        const visualChanges: VisualChange[] = changesArray.map((change: any) => {
          // Get user info from the collaboration data we fetched
          const userInfo = userMap.get(change.user_id);
          const userName = userInfo?.name || `User ${change.user_id.substring(0, 8)}`;
          const userEmail = userInfo?.email || 'unknown@example.com';

          return {
            id: change.id,
            type: change.change_type,
            userId: change.user_id,
            userName: userName,
            userEmail: userEmail,
            timestamp: new Date(change.created_at),
            position: {
              start: change.position_data?.start || 0,
              end: change.position_data?.end || 0
            },
            content: change.new_content,
            originalContent: change.original_content,
            newContent: change.new_content,
            changeData: change.change_data,
            positionData: change.position_data,
            confidence: change.change_data?.confidence || 0.8, // Default confidence if not provided
            status: change.status || 'pending' // Default to pending if not provided
          };
        });

        console.log('[VisualChangeTracker] Processed changes:', visualChanges);
        setChanges(visualChanges);
        onChangesLoaded?.(visualChanges);
      } else if (data.success && (!changesArray || changesArray.length === 0)) {
        // No changes found - this is valid
        console.log('[VisualChangeTracker] No changes found for document');
        setChanges([]);
        onChangesLoaded?.([]);
      } else {
        throw new Error(data.message || 'Invalid response format');
      }
    } catch (err) {
      console.error('[VisualChangeTracker] Error loading changes:', err);
      setError(err instanceof Error ? err.message : 'Failed to load changes');
    } finally {
      setIsLoading(false);
    }
  };

  // Event handlers
  const handleUserSelect = (userId: string | null) => {
    setSelectedUserId(userId);
    setSelectedChangeIndex(0);
    onChangeSelection?.(null);
    onUserSelection?.(userId);
  };

  const handleChangeNavigation = (direction: 'prev' | 'next') => {
    if (filteredChanges.length === 0) return;

    let newIndex = selectedChangeIndex;
    if (direction === 'prev') {
      newIndex = selectedChangeIndex > 0 ? selectedChangeIndex - 1 : filteredChanges.length - 1;
    } else {
      newIndex = selectedChangeIndex < filteredChanges.length - 1 ? selectedChangeIndex + 1 : 0;
    }

    setSelectedChangeIndex(newIndex);
    onChangeSelection?.(filteredChanges[newIndex]?.id || null);
  };

  const formatTimestamp = (timestamp: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - timestamp.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);

    if (diffHours < 24) {
      return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return timestamp.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const getChangeIcon = (type: string) => {
    switch (type) {
      // Basic operations
      case 'insert': return <PlusIcon className="h-3 w-3" />;
      case 'delete': return <MinusIcon className="h-3 w-3" />;
      case 'format': return <PencilIcon className="h-3 w-3" />;
      case 'move': return <ArrowsRightLeftIcon className="h-3 w-3" />;

      // Extended operations
      case 'replace': return <ArrowsRightLeftIcon className="h-3 w-3" />;
      case 'style': return <SwatchIcon className="h-3 w-3" />;
      case 'structure': return <Squares2X2Icon className="h-3 w-3" />;
      case 'annotation': return <ChatBubbleLeftIcon className="h-3 w-3" />;
      case 'metadata': return <TagIcon className="h-3 w-3" />;
      case 'table': return <TableCellsIcon className="h-3 w-3" />;
      case 'image': return <PhotoIcon className="h-3 w-3" />;
      case 'link': return <LinkIcon className="h-3 w-3" />;

      // Legal document specific
      case 'citation': return <ScaleIcon className="h-3 w-3" />;
      case 'clause': return <DocumentTextIcon className="h-3 w-3" />;
      case 'signature': return <IdentificationIcon className="h-3 w-3" />;
      case 'header_footer': return <DocumentIcon className="h-3 w-3" />;
      case 'page_break': return <DocumentIcon className="h-3 w-3" />;
      case 'line_number': return <HashtagIcon className="h-3 w-3" />;

      // Collaboration types
      case 'suggestion': return <ExclamationTriangleIcon className="h-3 w-3" />;
      case 'acceptance': return <HandThumbUpIcon className="h-3 w-3" />;
      case 'rejection': return <HandThumbDownIcon className="h-3 w-3" />;
      case 'comment': return <ChatBubbleLeftIcon className="h-3 w-3" />;

      default: return <PencilIcon className="h-3 w-3" />;
    }
  };

  if (isLoading) {
    return (
      <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 ${className}`}>
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-lex-accent" />
          <span className="text-sm text-gray-600 dark:text-gray-400">Loading changes...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white dark:bg-gray-800 border border-red-200 dark:border-red-700 rounded-lg p-4 ${className}`}>
        <div className="text-sm text-red-600 dark:text-red-400">
          Error: {error}
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg ${className}`}>
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white">
            Document Changes
          </h3>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="text-xs text-lex-accent hover:text-lex-accent-hover flex items-center space-x-1"
            >
              <span>{showAdvancedFilters ? 'Hide' : 'Show'} Filters</span>
            </button>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {filteredChanges.length} of {changes.length} changes
            </div>
          </div>
        </div>
      </div>

      {/* Advanced Filtering Toolbar */}
      {showAdvancedFilters && (
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 space-y-3">
          {/* Quick Filter Buttons */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Quick Filters:</span>
            <button
              onClick={() => setFilter(prev => ({ ...prev, legalSignificance: prev.legalSignificance === 'legal-only' ? 'all' : 'legal-only' }))}
              className={`px-2 py-1 text-xs rounded ${
                filter.legalSignificance === 'legal-only'
                  ? 'bg-amber-100 text-amber-800 border border-amber-300'
                  : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
              }`}
            >
              Legal Only
            </button>
            <button
              onClick={() => setFilter(prev => ({ ...prev, changeTypes: prev.changeTypes.includes('citation') ? [] : ['citation', 'clause'] }))}
              className={`px-2 py-1 text-xs rounded ${
                filter.changeTypes.includes('citation')
                  ? 'bg-lex-accent-soft text-lex-accent border border-lex-accent'
                  : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
              }`}
            >
              Citations & Clauses
            </button>
            <button
              onClick={() => setFilter(prev => ({ ...prev, confidenceMin: prev.confidenceMin >= 0.8 ? 0.5 : 0.8 }))}
              className={`px-2 py-1 text-xs rounded ${
                filter.confidenceMin >= 0.8
                  ? 'bg-green-100 text-green-800 border border-green-300'
                  : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
              }`}
            >
              High Confidence ({Math.round(filter.confidenceMin * 100)}%+)
            </button>
          </div>

          {/* Detailed Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Time Range
              </label>
              <select
                value={filter.timeRange}
                onChange={(e) => setFilter(prev => ({ ...prev, timeRange: e.target.value as any }))}
                className="w-full text-xs border border-gray-300 rounded px-2 py-1 bg-white dark:bg-gray-800 dark:border-gray-600"
              >
                <option value="all">All Time</option>
                <option value="last-hour">Last Hour</option>
                <option value="last-day">Last Day</option>
                <option value="last-week">Last Week</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Confidence Level
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={filter.confidenceMin}
                onChange={(e) => setFilter(prev => ({ ...prev, confidenceMin: parseFloat(e.target.value) }))}
                className="w-full"
              />
              <div className="text-xs text-gray-500 mt-1">{Math.round(filter.confidenceMin * 100)}% minimum</div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status
              </label>
              <select
                value={filter.status}
                onChange={(e) => setFilter(prev => ({ ...prev, status: e.target.value as any }))}
                className="w-full text-xs border border-gray-300 rounded px-2 py-1 bg-white dark:bg-gray-800 dark:border-gray-600"
              >
                <option value="all">All Changes</option>
                <option value="pending">Pending Review</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>

          {/* Keyboard Shortcuts Help */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-600">
            <div className="flex items-center space-x-4 text-xs text-gray-500">
              <div className="flex items-center space-x-1">
                <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-600 rounded text-xs">J</kbd>
                <span>Next</span>
              </div>
              <div className="flex items-center space-x-1">
                <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-600 rounded text-xs">K</kbd>
                <span>Previous</span>
              </div>
              <div className="flex items-center space-x-1">
                <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-600 rounded text-xs">Ctrl+A</kbd>
                <span>Select All</span>
              </div>
              <div className="flex items-center space-x-1">
                <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-600 rounded text-xs">Esc</kbd>
                <span>Clear</span>
              </div>
            </div>

            {/* Bulk Actions */}
            {navigation.selectedChangeIds.length > 0 && (
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-600">{navigation.selectedChangeIds.length} selected</span>
                <button className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700">
                  Accept All
                </button>
                <button className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700">
                  Reject All
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Collaborators List */}
      <div className="p-4 space-y-2">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">
            Contributors
          </h4>
          {selectedUserId && (
            <button
              onClick={() => handleUserSelect(null)}
              className="text-xs text-lex-accent hover:text-lex-accent-hover flex items-center space-x-1"
            >
              <EyeSlashIcon className="h-3 w-3" />
              <span>Show All</span>
            </button>
          )}
        </div>

        {collaborators.length === 0 ? (
          <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
            No changes found
          </div>
        ) : (
          <div className="space-y-2">
            {collaborators.map((collaborator) => (
              <div
                key={collaborator.userId}
                className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${
                  selectedUserId === collaborator.userId
                    ? 'bg-lex-accent-soft border border-lex-accent'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
                onClick={() => handleUserSelect(
                  selectedUserId === collaborator.userId ? null : collaborator.userId
                )}
              >
                <div className="flex items-center space-x-2 min-w-0 flex-1">
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: collaborator.color }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {collaborator.userName}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {collaborator.totalChanges} changes • {formatTimestamp(collaborator.lastChangeAt)}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-1 text-xs text-gray-500 dark:text-gray-400 flex-wrap">
                  {/* Basic operations */}
                  {collaborator.changeBreakdown.inserts > 0 && (
                    <span className="flex items-center space-x-1 text-green-600">
                      <PlusIcon className="h-3 w-3" />
                      <span>{collaborator.changeBreakdown.inserts}</span>
                    </span>
                  )}
                  {collaborator.changeBreakdown.deletes > 0 && (
                    <span className="flex items-center space-x-1 text-red-600">
                      <MinusIcon className="h-3 w-3" />
                      <span>{collaborator.changeBreakdown.deletes}</span>
                    </span>
                  )}
                  {collaborator.changeBreakdown.formats > 0 && (
                    <span className="flex items-center space-x-1 text-lex-accent">
                      <PencilIcon className="h-3 w-3" />
                      <span>{collaborator.changeBreakdown.formats}</span>
                    </span>
                  )}
                  {collaborator.changeBreakdown.moves > 0 && (
                    <span className="flex items-center space-x-1 text-purple-600">
                      <ArrowsRightLeftIcon className="h-3 w-3" />
                      <span>{collaborator.changeBreakdown.moves}</span>
                    </span>
                  )}

                  {/* Extended operations */}
                  {collaborator.changeBreakdown.replaces > 0 && (
                    <span className="flex items-center space-x-1 text-orange-600">
                      <ArrowsRightLeftIcon className="h-3 w-3" />
                      <span>{collaborator.changeBreakdown.replaces}</span>
                    </span>
                  )}
                  {collaborator.changeBreakdown.styles > 0 && (
                    <span className="flex items-center space-x-1 text-cyan-600">
                      <SwatchIcon className="h-3 w-3" />
                      <span>{collaborator.changeBreakdown.styles}</span>
                    </span>
                  )}
                  {collaborator.changeBreakdown.tables > 0 && (
                    <span className="flex items-center space-x-1 text-lex-accent">
                      <TableCellsIcon className="h-3 w-3" />
                      <span>{collaborator.changeBreakdown.tables}</span>
                    </span>
                  )}
                  {collaborator.changeBreakdown.images > 0 && (
                    <span className="flex items-center space-x-1 text-pink-600">
                      <PhotoIcon className="h-3 w-3" />
                      <span>{collaborator.changeBreakdown.images}</span>
                    </span>
                  )}

                  {/* Legal specific */}
                  {collaborator.changeBreakdown.citations > 0 && (
                    <span className="flex items-center space-x-1 text-yellow-600">
                      <ScaleIcon className="h-3 w-3" />
                      <span>{collaborator.changeBreakdown.citations}</span>
                    </span>
                  )}
                  {collaborator.changeBreakdown.clauses > 0 && (
                    <span className="flex items-center space-x-1 text-amber-600">
                      <DocumentTextIcon className="h-3 w-3" />
                      <span>{collaborator.changeBreakdown.clauses}</span>
                    </span>
                  )}

                  {/* Collaboration */}
                  {collaborator.changeBreakdown.suggestions > 0 && (
                    <span className="flex items-center space-x-1 text-yellow-500">
                      <ExclamationTriangleIcon className="h-3 w-3" />
                      <span>{collaborator.changeBreakdown.suggestions}</span>
                    </span>
                  )}
                  {collaborator.changeBreakdown.comments > 0 && (
                    <span className="flex items-center space-x-1 text-gray-600">
                      <ChatBubbleLeftIcon className="h-3 w-3" />
                      <span>{collaborator.changeBreakdown.comments}</span>
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Selected User Changes Navigation */}
      {selectedUserId && filteredChanges.length > 0 && (
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              {selectedCollaborator?.userName}'s Changes
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {selectedChangeIndex + 1} of {filteredChanges.length}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <button
              onClick={() => handleChangeNavigation('prev')}
              disabled={filteredChanges.length <= 1}
              className="flex items-center space-x-1 px-2 py-1 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeftIcon className="h-3 w-3" />
              <span>Previous</span>
            </button>

            <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
              {filteredChanges[selectedChangeIndex] && (
                <div>
                  {getChangeIcon(filteredChanges[selectedChangeIndex].type)}
                  <span className="ml-1">
                    {formatTimestamp(filteredChanges[selectedChangeIndex].timestamp)}
                  </span>
                </div>
              )}
            </div>

            <button
              onClick={() => handleChangeNavigation('next')}
              disabled={filteredChanges.length <= 1}
              className="flex items-center space-x-1 px-2 py-1 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span>Next</span>
              <ChevronRightIcon className="h-3 w-3" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisualChangeTracker;
